/**
 * WebAuthn JSON <-> binary. The server sends options as JSON with base64url
 * strings (`PublicKeyCredentialCreationOptionsJSON`); the browser API wants
 * `ArrayBuffer`s, and hands back credentials whose binary fields must go back
 * as base64url. The types are structural so this layer needs no DOM lib.
 */

import type { WebAuthnJson } from './domain';
import { base64UrlToBuffer, bytesToBase64Url, type BinaryLike } from './base64url';

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function bufferField(value: unknown): unknown {
  return typeof value === 'string' ? base64UrlToBuffer(value) : value;
}

function descriptors(list: unknown): unknown {
  if (!Array.isArray(list)) return list;
  return list.map((entry) => (isObject(entry) ? { ...entry, id: bufferField(entry['id']) } : entry));
}

/** Some servers wrap options as `{publicKey: {...}}`; the contract nests them in the response's `publicKey`. */
function unwrap(json: WebAuthnJson): Json {
  const inner = json['publicKey'];
  return isObject(inner) ? inner : json;
}

/** For `navigator.credentials.create({publicKey})`. */
export function creationOptionsFromJson(json: WebAuthnJson): Json {
  const options = unwrap(json);
  const user = isObject(options['user']) ? { ...options['user'], id: bufferField(options['user']['id']) } : options['user'];
  return {
    ...options,
    challenge: bufferField(options['challenge']),
    user,
    excludeCredentials: descriptors(options['excludeCredentials']),
  };
}

/** For `navigator.credentials.get({publicKey})`. */
export function requestOptionsFromJson(json: WebAuthnJson): Json {
  const options = unwrap(json);
  const out: Json = { ...options, challenge: bufferField(options['challenge']) };
  if ('allowCredentials' in options) out['allowCredentials'] = descriptors(options['allowCredentials']);
  return out;
}

/** The parts of a `PublicKeyCredential` this layer reads. */
export interface CredentialLike {
  id: string;
  rawId: BinaryLike;
  type: string;
  authenticatorAttachment?: string | null;
  response: {
    clientDataJSON: BinaryLike;
    attestationObject?: BinaryLike;
    authenticatorData?: BinaryLike;
    signature?: BinaryLike;
    userHandle?: BinaryLike | null;
    getTransports?: () => string[];
  };
  getClientExtensionResults?: () => Record<string, unknown>;
}

function common(credential: CredentialLike): Json {
  return {
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    clientExtensionResults:
      typeof credential.getClientExtensionResults === 'function' ? credential.getClientExtensionResults() : {},
  };
}

/** Registration: what `/mfa/webauthn/register/verify` takes as `credential`. */
export function attestationToJson(credential: CredentialLike): WebAuthnJson {
  const response = credential.response;
  if (!response.attestationObject) throw new Error('Not an attestation credential');
  const transports = typeof response.getTransports === 'function' ? response.getTransports() : [];
  return {
    ...common(credential),
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      attestationObject: bytesToBase64Url(response.attestationObject),
      transports,
    },
  };
}

/** Authentication: the `assertion` a step-up or login verify takes. */
export function assertionToJson(credential: CredentialLike): WebAuthnJson {
  const response = credential.response;
  if (!response.authenticatorData || !response.signature) throw new Error('Not an assertion credential');
  return {
    ...common(credential),
    response: {
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      signature: bytesToBase64Url(response.signature),
      userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
    },
  };
}

/**
 * A user's cancel (or timeout) on the browser prompt is `NotAllowedError`; it is
 * not a failure worth an error banner, so callers branch on it.
 */
export function isWebAuthnCancel(error: unknown): boolean {
  return isObject(error) && (error['name'] === 'NotAllowedError' || error['name'] === 'AbortError');
}
